import { Refresh } from "@mui/icons-material";
import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  AccessControl,
  RebuildRouteDropdownAppliedInfo,
  RebuildRouteDropdownCustomRebuildHeading,
  RebuildRouteDropdownMenu,
  RebuildRouteDropdownSkipCacheRow,
  RebuildRouteDropdownSubmitButton,
  RebuildRouteProviderSelector,
} from "@ryde/controls";
import {
  CourseRouteTypeEnum,
  formatRebuildRouteNoCacheButtonCopy,
  IRideDto,
  RebuildRouteTypeEnum,
  resolveRebuildRouteProvider,
} from "@ryde/shared";

import {
  RebuildRouteDropdownDefaultAction,
  RebuildRouteDropdownOptionsSelector,
  RebuildRouteDropdownTopButton,
} from "./parts";
import { RebuildRouteDropdownWrapper } from "./rebuild-route-dropdown.styled";
import { useRebuildRouteDropdown } from "./use-rebuild-route-dropdown";

interface IRebuildRouteDropdownProps {
  rideDto: Partial<IRideDto>;
}

export const RebuildRouteDropdown: FC<IRebuildRouteDropdownProps> = ({
  rideDto,
}) => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const isShuttleLikeRide =
    rideDto?.courseRouteType === CourseRouteTypeEnum.Shuttle ||
    rideDto?.courseRouteType === CourseRouteTypeEnum.Flex;
  const isCourseRide = Boolean(rideDto.courseId);
  const showStationsReorderOptions = !(isCourseRide && isShuttleLikeRide);

  const {
    open,
    isLoading,
    systemDefaultProvider,
    savedLastUsedProvider,
    currentRebuildProvider,
    rebuildRouteTooltip,
    selectedProvider,
    setSelectedProvider,
    avoidTolls,
    setAvoidTolls,
    skipMlPath,
    setSkipMlPath,
    routeOption,
    setRouteOption,
    supportsAvoidTolls,
    systemDefaultAvoidTolls,
    hasCustomRebuildType,
    lastSelectedRebuildType,
    lastRouteRebuildAt,
    handleDropdownToggle,
    handleClose: handleCloseFromHook,
    handleTopDefaultRebuild,
    handleDefaultRebuild,
    handleRebuild,
  } = useRebuildRouteDropdown(rideDto);

  const rerouteProvider = resolveRebuildRouteProvider({
    selectedProvider,
    lastUsedProvider: savedLastUsedProvider,
    systemDefaultProvider,
  });
  const rerouteRebuildType =
    routeOption || RebuildRouteTypeEnum.ProviderOptimization;

  const noCacheButtonCopy = useMemo(
    () =>
      formatRebuildRouteNoCacheButtonCopy({
        provider: rerouteProvider,
        rebuildType: rerouteRebuildType,
        t,
      }),
    [rerouteProvider, rerouteRebuildType, t],
  );

  const handleClose = (event: Event | React.SyntheticEvent): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    handleCloseFromHook(event);
  };

  return (
    <AccessControl entity="ride" action="update">
      {rideDto?.courseRouteType !== CourseRouteTypeEnum.Shuttle && (
        <RebuildRouteDropdownWrapper $isOpen={open}>
          <RebuildRouteDropdownTopButton
            anchorRef={anchorRef}
            isLoading={isLoading}
            hasCustomRebuildType={hasCustomRebuildType}
            rebuildRouteTooltip={rebuildRouteTooltip}
            onDefaultRebuild={handleTopDefaultRebuild}
            onDropdownToggle={handleDropdownToggle}
          />

          <Popper
            open={open}
            anchorEl={anchorRef.current}
            transition
            disablePortal
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper elevation={8}>
                  <ClickAwayListener onClickAway={handleClose}>
                    <RebuildRouteDropdownMenu>
                      <RebuildRouteDropdownAppliedInfo
                        lastUsedProvider={
                          currentRebuildProvider ?? savedLastUsedProvider
                        }
                        systemDefaultProvider={systemDefaultProvider}
                        routeRebuildAt={rideDto.metadata?.routeRebuildAt}
                        sessionRouteRebuildAt={lastRouteRebuildAt}
                        rebuildRouteType={rideDto.metadata?.rebuildRouteType}
                        lastSelectedRebuildType={lastSelectedRebuildType}
                        usedCachedRouteId={rideDto.metadata?.usedCachedRouteId}
                        customStationRouteId={
                          rideDto.metadata?.customStationRouteId
                        }
                        routeRebuildSource={
                          rideDto.metadata?.routeRebuildSource
                        }
                        ignoreMLPathReason={
                          rideDto.metadata?.ignoreMLPathReason
                        }
                        rideOpenerId={rideDto.id}
                        onClose={handleClose}
                      />

                      <RebuildRouteDropdownDefaultAction
                        rideDto={rideDto}
                        isLoading={isLoading}
                        lastUsedProvider={
                          currentRebuildProvider ?? savedLastUsedProvider
                        }
                        systemDefaultProvider={systemDefaultProvider}
                        onRebuild={handleDefaultRebuild}
                      />

                      <RebuildRouteDropdownCustomRebuildHeading />

                      <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--providers">
                        <RebuildRouteProviderSelector
                          selectedProvider={selectedProvider}
                          systemDefaultProvider={systemDefaultProvider}
                          lastUsedProvider={currentRebuildProvider}
                          onProviderChange={setSelectedProvider}
                        />
                      </div>

                      <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--options">
                        <RebuildRouteDropdownOptionsSelector
                          supportsAvoidTolls={supportsAvoidTolls}
                          avoidTolls={avoidTolls}
                          isDefaultAvoidTolls={systemDefaultAvoidTolls}
                          routeOption={routeOption}
                          selectedProvider={rerouteProvider}
                          showStationsReorderOptions={
                            showStationsReorderOptions
                          }
                          onAvoidTollsChange={setAvoidTolls}
                          onRouteOptionChange={setRouteOption}
                        />

                        <RebuildRouteDropdownSkipCacheRow
                          checked={skipMlPath}
                          label={t("general.SKIP_SAVED_ML_PATH")}
                          tooltip={t("general.SKIP_SAVED_ML_PATH_TOOLTIP")}
                          onChange={setSkipMlPath}
                        />

                        <RebuildRouteDropdownSubmitButton
                          label={noCacheButtonCopy.title}
                          hint={noCacheButtonCopy.hint}
                          tooltip={t("general.RE_ROUTE_NO_CACHE_TOOLTIP")}
                          startIcon={<Refresh />}
                          isLoading={isLoading}
                          onClick={handleRebuild}
                        />
                      </div>
                    </RebuildRouteDropdownMenu>
                  </ClickAwayListener>
                </Paper>
              </Fade>
            )}
          </Popper>
        </RebuildRouteDropdownWrapper>
      )}
    </AccessControl>
  );
};
